/**
 * Workers Command
 * Publish and manage Worker packages (agent environment archives) on the 0.works marketplace.
 * Similar to npm publish — packages cwd into a zip, respects .workersignore, uploads to 0.works.
 */

import { Command } from 'commander';
import { readFileSync, writeFileSync, existsSync, statSync, readdirSync, mkdirSync, createWriteStream } from 'fs';
import { join, resolve, basename } from 'path';
import { createHash } from 'crypto';
import { tmpdir } from 'os';
import { execSync } from 'child_process';
import axios from 'axios';
import {
  uploadWorkerPackage,
  publishWorkerPackage,
  getWorkerBySlug,
  listMyPublishedWorkers,
  listWorkerPackages,
  deleteWorkerPackage,
  listWorkerVersions,
  purchaseWorker,
  downloadWorker,
  listMyPurchasedWorkers,
  getUserProfile,
  type WorkerPackage,
} from '../api/workers-marketplace.js';
import { isAuthenticated } from '../utils/config.js';
import * as output from '../utils/output.js';

function requireAuth(): void {
  if (!isAuthenticated()) {
    output.error('Not logged in. Run `paean login` first.');
    process.exit(1);
  }
}

const DEFAULT_IGNORE_PATTERNS = [
  'node_modules',
  '.git',
  '.DS_Store',
  '.env',
  '.env.*',
  '*.log',
  'dist',
  'build',
  '__pycache__',
  '.pytest_cache',
  '.mypy_cache',
  '.tox',
  '.venv',
  'venv',
  '.idea',
  '.vscode',
  '*.pyc',
  '*.pyo',
  '*.swp',
  '*.swo',
  'Thumbs.db',
  '.workersignore',
];

const MANIFEST_FILE = 'workers.json';
const IGNORE_FILE = '.workersignore';
const MAX_PACKAGE_SIZE = 500 * 1024 * 1024; // 500 MB

interface WorkerManifest {
  name: string;
  slug?: string;
  version: string;
  description?: string;
  shortDescription?: string;
  changelog?: string;
  compatibleAgents?: string[];
  categories?: string[];
  priceType?: 'free' | 'credits' | 'usd';
  priceAmount?: number;
  digitalTwinHint?: string;
}

function loadIgnorePatterns(dir: string): string[] {
  const patterns = [...DEFAULT_IGNORE_PATTERNS];
  const ignorePath = join(dir, IGNORE_FILE);
  if (existsSync(ignorePath)) {
    const content = readFileSync(ignorePath, 'utf-8');
    const lines = content.split('\n')
      .map(l => l.trim())
      .filter(l => l && !l.startsWith('#'));
    patterns.push(...lines);
  }
  return patterns;
}

function matchesPattern(filePath: string, pattern: string): boolean {
  if (pattern.endsWith('/')) {
    return filePath.startsWith(pattern) || filePath.includes('/' + pattern);
  }
  const name = basename(filePath);
  if (pattern.includes('*')) {
    const regex = new RegExp('^' + pattern.replace(/\./g, '\\.').replace(/\*/g, '.*') + '$');
    return regex.test(name) || regex.test(filePath);
  }
  return name === pattern || filePath === pattern || filePath.startsWith(pattern + '/');
}

function shouldIgnore(filePath: string, patterns: string[]): boolean {
  for (const pattern of patterns) {
    if (matchesPattern(filePath, pattern)) return true;
  }
  return false;
}

function collectFiles(dir: string, patterns: string[], base = ''): string[] {
  const files: string[] = [];
  for (const entry of readdirSync(dir, { withFileTypes: true })) {
    const relPath = base ? `${base}/${entry.name}` : entry.name;
    if (shouldIgnore(relPath, patterns)) continue;
    const fullPath = join(dir, entry.name);
    if (entry.isDirectory()) {
      files.push(...collectFiles(fullPath, patterns, relPath));
    } else if (entry.isFile()) {
      files.push(relPath);
    }
  }
  return files;
}

function computeDirHash(dir: string, files: string[]): string {
  const hash = createHash('sha256');
  for (const f of files.sort()) {
    hash.update(f);
    hash.update(readFileSync(join(dir, f)));
  }
  return hash.digest('hex');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function loadManifest(dir: string): WorkerManifest | null {
  const manifestPath = join(dir, MANIFEST_FILE);
  if (!existsSync(manifestPath)) return null;
  try {
    return JSON.parse(readFileSync(manifestPath, 'utf-8'));
  } catch {
    return null;
  }
}

function saveManifest(dir: string, manifest: WorkerManifest): void {
  const manifestPath = join(dir, MANIFEST_FILE);
  writeFileSync(manifestPath, JSON.stringify(manifest, null, 2) + '\n');
}

export const workersCommand = new Command('workers')
  .description('Publish and manage Worker packages on 0.works marketplace')
  .action(async () => {
    requireAuth();
    await listMy();
  });

// ── paean workers whoami ────────────────────────────────────────────────

workersCommand
  .command('whoami')
  .description('Show current authenticated user')
  .option('--json', 'Output as JSON')
  .action(async (options) => {
    requireAuth();
    try {
      const result = await getUserProfile();
      const u = result.user;

      if (options.json) {
        output.json(u);
        return;
      }

      output.header('Current User');
      output.tableRow('Email', u.email);
      if (u.displayName) output.tableRow('Name', u.displayName);
      if (u.tier) output.tableRow('Tier', u.tier);
      if (u.credits !== undefined) output.tableRow('Credits', String(u.credits));
      output.newline();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Failed: ${msg}`);
      process.exit(1);
    }
  });

// ── paean workers init ──────────────────────────────────────────────────

workersCommand
  .command('init')
  .description('Initialize a workers.json manifest in the current directory')
  .option('--name <name>', 'Package display name')
  .action(async (options) => {
    const dir = process.cwd();
    const manifestPath = join(dir, MANIFEST_FILE);

    if (existsSync(manifestPath)) {
      output.warning(`${MANIFEST_FILE} already exists.`);
      return;
    }

    const name = options.name || basename(dir);
    const manifest: WorkerManifest = {
      name,
      version: '1.0.0',
      description: '',
      shortDescription: '',
      compatibleAgents: ['cursor', 'claude-code'],
      categories: ['coding'],
      priceType: 'free',
      priceAmount: 0,
    };

    writeFileSync(manifestPath, JSON.stringify(manifest, null, 2) + '\n');
    output.success(`Created ${MANIFEST_FILE}`);

    if (!existsSync(join(dir, IGNORE_FILE))) {
      writeFileSync(
        join(dir, IGNORE_FILE),
        '# Files/directories to exclude from worker package\nnode_modules\n.git\n.env\n.env.*\ndist\nbuild\n*.log\n'
      );
      output.success(`Created ${IGNORE_FILE}`);
    }

    output.dim(`\nEdit ${MANIFEST_FILE} then run: paean workers publish`);
  });

// ── paean workers pack ──────────────────────────────────────────────────

workersCommand
  .command('pack')
  .description('Create a .zip package from the current directory (dry-run, no upload)')
  .option('-o, --output <path>', 'Output zip path')
  .action(async (options) => {
    const dir = process.cwd();
    const manifest = loadManifest(dir);
    if (!manifest) {
      output.error(`No ${MANIFEST_FILE} found. Run: paean workers init`);
      process.exit(1);
    }

    const patterns = loadIgnorePatterns(dir);
    const files = collectFiles(dir, patterns);

    output.info(`Packing: ${manifest.name} v${manifest.version}`);
    output.dim(`${files.length} files`);

    const slug = manifest.slug || manifest.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const outputPath = options.output || join(dir, `${slug}-${manifest.version}.zip`);

    const fileList = files.map(f => `"${f}"`).join(' ');
    try {
      execSync(`cd "${dir}" && zip -q "${outputPath}" ${fileList}`, { stdio: 'pipe' });
    } catch {
      execSync(`cd "${dir}" && tar czf "${outputPath}.tar.gz" ${fileList}`, { stdio: 'pipe' });
      output.warning('zip not available, created .tar.gz instead');
      return;
    }

    const stat = statSync(outputPath);
    output.success(`Package created: ${outputPath}`);
    output.dim(`Size: ${formatSize(stat.size)}`);

    if (stat.size > MAX_PACKAGE_SIZE) {
      output.warning(`Package exceeds ${formatSize(MAX_PACKAGE_SIZE)} limit`);
    }
  });

// ── paean workers publish ───────────────────────────────────────────────

workersCommand
  .command('publish')
  .description('Package and publish the current directory to 0.works marketplace')
  .option('--draft', 'Upload as draft without publishing')
  .option('--zip <path>', 'Upload an existing zip file instead of packing')
  .option('--changelog <text>', 'Changelog / release notes for this version')
  .action(async (options) => {
    requireAuth();

    const dir = process.cwd();
    const manifest = loadManifest(dir);
    if (!manifest) {
      output.error(`No ${MANIFEST_FILE} found. Run: paean workers init`);
      process.exit(1);
    }

    if (!manifest.name) {
      output.error('name is required in workers.json');
      process.exit(1);
    }

    let zipPath = options.zip;
    let tempZip = false;

    if (!zipPath) {
      const patterns = loadIgnorePatterns(dir);
      const files = collectFiles(dir, patterns);

      if (files.length === 0) {
        output.error('No files to package');
        process.exit(1);
      }

      output.info(`Packing ${manifest.name} v${manifest.version}...`);
      output.dim(`${files.length} files`);

      zipPath = join(tmpdir(), `worker-${Date.now()}.zip`);
      tempZip = true;

      const fileList = files.map(f => `"${f}"`).join(' ');
      try {
        execSync(`cd "${dir}" && zip -q -r "${zipPath}" ${fileList}`, { stdio: 'pipe' });
      } catch {
        output.error('Failed to create zip. Ensure "zip" is installed.');
        process.exit(1);
      }
    }

    const stat = statSync(zipPath);
    output.dim(`Package size: ${formatSize(stat.size)}`);

    if (stat.size > MAX_PACKAGE_SIZE) {
      output.error(`Package too large (${formatSize(stat.size)}). Max: ${formatSize(MAX_PACKAGE_SIZE)}`);
      process.exit(1);
    }

    const spin = output.spinner('Uploading...').start();

    try {
      const metadata: Record<string, unknown> = {
        displayName: manifest.name,
        version: manifest.version,
        shortDescription: manifest.shortDescription || '',
        description: manifest.description || '',
        compatibleAgents: manifest.compatibleAgents || [],
        categories: manifest.categories || [],
        priceType: manifest.priceType || 'free',
        priceAmount: manifest.priceAmount || 0,
      };
      if (manifest.slug) metadata.slug = manifest.slug;
      if (manifest.digitalTwinHint) metadata.digitalTwinHint = manifest.digitalTwinHint;
      if (options.changelog) metadata.changelog = options.changelog;

      const result = await uploadWorkerPackage(zipPath, metadata);
      spin.stop();

      const worker = result.worker;
      const isUpdate = result.isVersionUpdate;

      if (!options.draft) {
        if (!isUpdate) {
          await publishWorkerPackage(worker.id);
        }
        if (isUpdate) {
          output.success(`Updated: ${worker.displayName} → v${worker.version}`);
          output.tableRow('Slug', worker.slug);
          output.dim('Existing worker updated with new version');
        } else {
          output.success(`Published: ${worker.displayName} v${worker.version}`);
          output.tableRow('Slug', worker.slug);
          output.dim('Status: published (pending review)');
        }
      } else {
        output.success(`Uploaded as draft: ${worker.displayName} v${worker.version}`);
        output.tableRow('ID', worker.id);
        output.dim(`Publish later: paean workers publish-id ${worker.id}`);
      }

      output.dim(`\nView: https://0.works/workers/${worker.slug}`);
      output.newline();
    } catch (error) {
      spin.stop();
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Upload failed: ${msg}`);
      process.exit(1);
    } finally {
      if (tempZip) {
        try { execSync(`rm -f "${zipPath}"`, { stdio: 'pipe' }); } catch {}
      }
    }
  });

// ── paean workers publish-id ────────────────────────────────────────────

workersCommand
  .command('publish-id <id>')
  .description('Publish a previously uploaded draft worker by ID')
  .action(async (id: string) => {
    requireAuth();
    try {
      const result = await publishWorkerPackage(id);
      output.success(`Published: ${result.worker.displayName}`);
      output.dim('Status: published (pending review)');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Publish failed: ${msg}`);
      process.exit(1);
    }
  });

// ── paean workers update ────────────────────────────────────────────────

workersCommand
  .command('update [bump]')
  .description('Bump version and publish in one step (default: patch)')
  .option('--changelog <text>', 'Changelog / release notes')
  .option('--draft', 'Upload as draft without publishing')
  .action(async (bump: string | undefined, options) => {
    requireAuth();

    const dir = process.cwd();
    const manifest = loadManifest(dir);
    if (!manifest) {
      output.error(`No ${MANIFEST_FILE} found. Run: paean workers init`);
      process.exit(1);
    }

    // Parse current version and bump
    const bumpType = bump || 'patch';
    const parts = (manifest.version || '1.0.0').split('.').map(Number);
    if (parts.length < 3 || parts.some(isNaN)) {
      output.error(`Invalid version format: ${manifest.version}. Expected semver (x.y.z)`);
      process.exit(1);
    }

    switch (bumpType) {
      case 'major':
        parts[0]++;
        parts[1] = 0;
        parts[2] = 0;
        break;
      case 'minor':
        parts[1]++;
        parts[2] = 0;
        break;
      case 'patch':
        parts[2]++;
        break;
      default:
        if (/^\d+\.\d+\.\d+/.test(bumpType)) {
          const explicit = bumpType.split('.').map(Number);
          parts[0] = explicit[0];
          parts[1] = explicit[1];
          parts[2] = explicit[2];
        } else {
          output.error(`Invalid bump type: ${bumpType}. Use major, minor, patch, or explicit version (e.g. 2.0.0)`);
          process.exit(1);
        }
    }

    const oldVersion = manifest.version;
    const newVersion = parts.join('.');
    manifest.version = newVersion;

    saveManifest(dir, manifest);
    output.info(`Version: ${oldVersion} -> ${newVersion}`);

    const patterns = loadIgnorePatterns(dir);
    const files = collectFiles(dir, patterns);

    if (files.length === 0) {
      output.error('No files to package');
      process.exit(1);
    }

    output.dim(`${files.length} files`);

    const zipPath = join(tmpdir(), `worker-${Date.now()}.zip`);
    const fileList = files.map(f => `"${f}"`).join(' ');
    try {
      execSync(`cd "${dir}" && zip -q -r "${zipPath}" ${fileList}`, { stdio: 'pipe' });
    } catch {
      output.error('Failed to create zip. Ensure "zip" is installed.');
      process.exit(1);
    }

    const spin = output.spinner('Publishing...').start();

    try {
      const metadata: Record<string, unknown> = {
        displayName: manifest.name,
        version: newVersion,
        shortDescription: manifest.shortDescription || '',
        description: manifest.description || '',
        compatibleAgents: manifest.compatibleAgents || [],
        categories: manifest.categories || [],
        priceType: manifest.priceType || 'free',
        priceAmount: manifest.priceAmount || 0,
      };
      if (manifest.slug) metadata.slug = manifest.slug;
      if (options.changelog) metadata.changelog = options.changelog;

      const result = await uploadWorkerPackage(zipPath, metadata);
      spin.stop();

      const worker = result.worker;
      if (!options.draft && !result.isVersionUpdate) {
        await publishWorkerPackage(worker.id);
      }

      output.success(`Published: ${worker.displayName} v${newVersion}`);
      output.dim(`https://0.works/workers/${worker.slug}`);
      output.newline();
    } catch (error) {
      spin.stop();
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Update failed: ${msg}`);
      process.exit(1);
    } finally {
      try { execSync(`rm -f "${zipPath}"`, { stdio: 'pipe' }); } catch {}
    }
  });

// ── paean workers inspect ───────────────────────────────────────────────

workersCommand
  .command('inspect [dir]')
  .description('Inspect directory contents and validate workers.json manifest')
  .action(async (dir?: string) => {
    const targetDir = resolve(dir || '.');
    const manifest = loadManifest(targetDir);

    output.header('Worker Package Inspection');

    if (!manifest) {
      output.error(`No ${MANIFEST_FILE} found in ${targetDir}`);
      output.dim('Run: paean workers init');
      return;
    }

    const issues: string[] = [];
    const warnings: string[] = [];

    if (!manifest.name) issues.push('name is required');
    if (!manifest.version) issues.push('version is required');
    if (!manifest.version?.match(/^\d+\.\d+\.\d+/)) warnings.push('version should follow semver (x.y.z)');
    if (!manifest.shortDescription) warnings.push('shortDescription is recommended');
    if (!manifest.description) warnings.push('description is recommended for discoverability');
    if (!manifest.compatibleAgents?.length) warnings.push('compatibleAgents should list supported agents');
    if (!manifest.categories?.length) warnings.push('categories help users find your worker');

    output.tableRow('Name', manifest.name);
    output.tableRow('Version', manifest.version);
    output.tableRow('Price', manifest.priceType === 'free' ? 'Free' : `${manifest.priceAmount} ${manifest.priceType}`);
    if (manifest.compatibleAgents?.length) {
      output.tableRow('Agents', manifest.compatibleAgents.join(', '));
    }
    if (manifest.categories?.length) {
      output.tableRow('Tags', manifest.categories.join(', '));
    }

    const patterns = loadIgnorePatterns(targetDir);
    const files = collectFiles(targetDir, patterns);
    let totalSize = 0;
    for (const f of files) {
      try { totalSize += statSync(join(targetDir, f)).size; } catch {}
    }

    output.tableRow('Files', String(files.length));
    output.tableRow('Size', `${formatSize(totalSize)} (uncompressed)`);

    const hash = computeDirHash(targetDir, files);
    output.tableRow('Hash', `${hash.substring(0, 16)}...`);

    if (totalSize > MAX_PACKAGE_SIZE) {
      issues.push(`Package too large: ${formatSize(totalSize)} > ${formatSize(MAX_PACKAGE_SIZE)}`);
    }

    const hasReadme = files.some(f => f.toLowerCase().startsWith('readme'));
    if (!hasReadme) warnings.push('Consider adding a README.md');

    if (issues.length > 0) {
      output.newline();
      output.error('Errors:');
      issues.forEach(i => output.listItem(output.colors.error(i), 1));
    }
    if (warnings.length > 0) {
      output.newline();
      output.warning('Warnings:');
      warnings.forEach(w => output.listItem(output.colors.warning(w), 1));
    }
    if (issues.length === 0 && warnings.length === 0) {
      output.newline();
      output.success('No issues found.');
    }

    output.newline();
  });

// ── paean workers list ──────────────────────────────────────────────────

workersCommand
  .command('list')
  .alias('ls')
  .description('List my published workers')
  .option('--json', 'Output as JSON')
  .action(async (options) => {
    requireAuth();
    await listMy(options.json);
  });

// ── paean workers search ────────────────────────────────────────────────

workersCommand
  .command('search [query]')
  .description('Search the workers marketplace')
  .option('--agent <agent>', 'Filter by compatible agent')
  .option('--category <cat>', 'Filter by category')
  .option('--json', 'Output as JSON')
  .action(async (query: string | undefined, options) => {
    try {
      const params: Record<string, string> = {};
      if (query) params.search = query;
      if (options.agent) params.agent = options.agent;
      if (options.category) params.category = options.category;

      const result = await listWorkerPackages(params);

      if (options.json) {
        output.json(result.items);
        return;
      }

      if (result.items.length === 0) {
        output.dim('No workers found.');
        return;
      }

      output.header(`Workers Marketplace (${result.total} results)`);
      printWorkerTable(result.items);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Search failed: ${msg}`);
      process.exit(1);
    }
  });

// ── paean workers info ──────────────────────────────────────────────────

workersCommand
  .command('info <slug>')
  .description('Show detailed information about a worker package')
  .option('--json', 'Output as JSON')
  .action(async (slug: string, options) => {
    try {
      const result = await getWorkerBySlug(slug);
      const w = result.worker;

      if (options.json) {
        output.json(w);
        return;
      }

      output.header(w.displayName);
      output.tableRow('Slug', `${w.slug}  |  v${w.version}  |  ${w.status}`);
      if (w.shortDescription) {
        output.newline();
        console.log(`  ${w.shortDescription}`);
      }
      output.tableRow('Price', w.priceType === 'free' ? 'Free' : `${w.priceAmount} ${w.priceType}`);
      output.tableRow('Downloads', `${w.downloadCount}  |  Rating: ${Number(w.avgRating).toFixed(1)}/5 (${w.reviewCount} reviews)`);
      output.tableRow('Size', formatSize(Number(w.fileSize)));
      if (w.compatibleAgents?.length) {
        output.tableRow('Compatible', w.compatibleAgents.join(', '));
      }
      if (w.categories?.length) {
        output.tableRow('Categories', w.categories.join(', '));
      }
      output.tableRow('Published', new Date(w.createdAt).toLocaleDateString());
      output.dim(`\nhttps://0.works/workers/${w.slug}`);
      output.newline();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Failed: ${msg}`);
      process.exit(1);
    }
  });

// ── paean workers install / add ─────────────────────────────────────────

workersCommand
  .command('install <slug>')
  .alias('add')
  .description('Download and install a worker package from the marketplace')
  .option('-v, --version <version>', 'Install a specific version')
  .option('-o, --output <dir>', 'Output directory (default: .workers/<slug>)')
  .option('--json', 'Output download info as JSON')
  .action(async (slug: string, options) => {
    requireAuth();

    const spin = output.spinner(`Fetching ${slug}...`).start();

    try {
      // Get worker info
      const info = await getWorkerBySlug(slug);
      const w = info.worker;

      // Get download URL (API checks purchase status)
      const dlResult = await downloadWorker(slug, options.version);
      spin.stop();

      if (options.json) {
        output.json({ worker: w, downloadUrl: dlResult.downloadUrl });
        return;
      }

      // Download the zip file
      const outputDir = options.output || join('.workers', slug);
      mkdirSync(outputDir, { recursive: true });

      const zipPath = join(tmpdir(), `worker-dl-${Date.now()}.zip`);

      const dlSpin = output.spinner('Downloading...').start();
      try {
        const response = await axios.get(dlResult.downloadUrl, { responseType: 'stream' });
        const writer = createWriteStream(zipPath);
        response.data.pipe(writer);
        await new Promise<void>((resolve, reject) => {
          writer.on('finish', resolve);
          writer.on('error', reject);
        });
        dlSpin.stop();
      } catch (err) {
        dlSpin.stop();
        output.error(`Download failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        process.exit(1);
      }

      // Extract
      const extractSpin = output.spinner('Extracting...').start();
      try {
        execSync(`unzip -o -q "${zipPath}" -d "${outputDir}"`, { stdio: 'pipe' });
        extractSpin.stop();
      } catch {
        extractSpin.stop();
        output.error('Failed to extract. Ensure "unzip" is installed.');
        process.exit(1);
      } finally {
        try { execSync(`rm -f "${zipPath}"`, { stdio: 'pipe' }); } catch {}
      }

      output.success(`Installed: ${w.displayName} v${options.version || w.version}`);
      output.tableRow('Location', resolve(outputDir));
      output.tableRow('Size', formatSize(Number(w.fileSize)));
      output.newline();
    } catch (error) {
      spin.stop();
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (msg.includes('purchase') || msg.includes('402')) {
        output.error(`Worker "${slug}" requires purchase first.`);
        output.dim(`  Run: paean workers purchase ${slug}`);
      } else {
        output.errorWithHint(`Install failed: ${msg}`);
      }
      process.exit(1);
    }
  });

// ── paean workers purchase ──────────────────────────────────────────────

workersCommand
  .command('purchase <slug>')
  .alias('buy')
  .description('Purchase a paid worker from the marketplace')
  .option('--json', 'Output as JSON')
  .action(async (slug: string, options) => {
    requireAuth();

    try {
      // Get worker info first
      const info = await getWorkerBySlug(slug);
      const w = info.worker;

      if (w.priceType === 'free') {
        output.success(`"${w.displayName}" is free! No purchase needed.`);
        output.dim(`  Run: paean workers install ${slug}`);
        return;
      }

      output.info(`Purchasing: ${w.displayName} v${w.version}`);
      output.tableRow('Price', `${w.priceAmount} ${w.priceType}`);
      output.newline();

      const spin = output.spinner('Processing purchase...').start();
      const result = await purchaseWorker(slug);
      spin.stop();

      if (options.json) {
        output.json(result);
        return;
      }

      output.success(`Purchased: ${w.displayName}`);
      output.dim(`  Install now: paean workers install ${slug}`);
      output.newline();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (msg.includes('insufficient') || msg.includes('402')) {
        output.error('Insufficient credits.');
        output.dim('  Top up at: https://app.paean.ai/settings/billing');
      } else if (msg.includes('already purchased') || msg.includes('already own')) {
        output.warning('You already own this worker.');
        output.dim(`  Run: paean workers install ${slug}`);
      } else {
        output.errorWithHint(`Purchase failed: ${msg}`);
      }
      process.exit(1);
    }
  });

// ── paean workers my ────────────────────────────────────────────────────

const myCommand = workersCommand
  .command('my')
  .description('Manage your workers (published and purchased)');

myCommand
  .command('published')
  .alias('pub')
  .description('List my published workers')
  .option('--json', 'Output as JSON')
  .action(async (options) => {
    requireAuth();
    await listMy(options.json);
  });

myCommand
  .command('purchased')
  .alias('bought')
  .description('List my purchased workers')
  .option('--json', 'Output as JSON')
  .action(async (options) => {
    requireAuth();
    try {
      const result = await listMyPurchasedWorkers();

      if (options.json) {
        output.json(result.items);
        return;
      }

      if (result.items.length === 0) {
        output.dim('No purchased workers.');
        output.dim('Browse: paean workers search');
        return;
      }

      output.header(`My Purchased Workers (${result.total})`);
      for (const p of result.items) {
        const name = p.displayName || p.slug || p.workerId;
        const price = p.pricePaid !== undefined ? `${p.pricePaid} ${p.priceType || ''}` : '';
        const date = new Date(p.createdAt).toLocaleDateString();
        console.log(`  ${output.colors.info((p.slug || p.workerId).padEnd(35))} ${name.substring(0, 30).padEnd(32)} ${output.colors.dim(`${price.padEnd(15)} ${date}`)}`);
      }
      output.newline();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Failed: ${msg}`);
      process.exit(1);
    }
  });

myCommand.action(async () => {
  requireAuth();
  await listMy();
});

// ── paean workers unpublish ─────────────────────────────────────────────

workersCommand
  .command('unpublish <id>')
  .description('Remove a published worker (soft delete)')
  .action(async (id: string) => {
    requireAuth();
    try {
      const result = await deleteWorkerPackage(id);
      output.success(`Removed: ${result.worker.displayName}`);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Failed: ${msg}`);
      process.exit(1);
    }
  });

// ── paean workers versions ──────────────────────────────────────────────

workersCommand
  .command('versions <slug>')
  .description('List all published versions of a worker')
  .option('--json', 'Output as JSON')
  .action(async (slug: string, options) => {
    try {
      const result = await listWorkerVersions(slug);

      if (options.json) {
        output.json(result);
        return;
      }

      if (result.items.length === 0) {
        output.dim('No versions found.');
        return;
      }

      output.header(`Versions of ${slug} (${result.total} total)`);
      output.dim(`  ${'VERSION'.padEnd(12)} ${'SIZE'.padEnd(10)} ${'DATE'.padEnd(14)} CHANGELOG`);
      output.dim('  ' + '─'.repeat(60));

      for (const v of result.items) {
        const isCurrent = v.version === result.currentVersion;
        const versionStr = isCurrent ? `${v.version} *` : v.version;
        const size = Number(v.fileSize) > 0
          ? `${(Number(v.fileSize) / (1024 * 1024)).toFixed(1)} MB`
          : '—';
        const date = new Date(v.createdAt).toLocaleDateString();
        const changelog = v.changelog ? v.changelog.substring(0, 40) : '';
        const line = `  ${versionStr.padEnd(12)} ${size.padEnd(10)} ${date.padEnd(14)} ${output.colors.dim(changelog)}`;
        console.log(isCurrent ? output.colors.info(line) : line);
      }
      output.newline();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      output.errorWithHint(`Failed: ${msg}`);
      process.exit(1);
    }
  });

// ── Helpers ─────────────────────────────────────────────────────────────

function printWorkerTable(items: WorkerPackage[]) {
  for (const w of items) {
    const price = w.priceType === 'free'
      ? output.colors.success('FREE')
      : output.colors.warning(`${w.priceAmount} ${w.priceType}`);
    const rating = Number(w.avgRating) > 0 ? `${Number(w.avgRating).toFixed(1)}/5` : '-';
    const agents = (w.compatibleAgents || []).slice(0, 3).join(', ');

    console.log(`  ${output.colors.info(w.slug.padEnd(35))} ${w.displayName.substring(0, 30).padEnd(32)} ${price.padEnd(15)} ${output.colors.dim(`${rating.padEnd(6)} ${String(w.downloadCount).padEnd(6)} ${agents}`)}`);
  }
  output.newline();
}

async function listMy(json = false): Promise<void> {
  try {
    const result = await listMyPublishedWorkers();

    if (json) {
      output.json(result.items);
      return;
    }

    if (result.items.length === 0) {
      output.dim('No published workers.');
      output.dim('Publish one: paean workers publish');
      return;
    }

    output.header(`My Workers (${result.total})`);
    printWorkerTable(result.items);
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Unknown error';
    output.errorWithHint(`Failed: ${msg}`);
    process.exit(1);
  }
}
